import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, Users, CalendarDays, Truck, Clock3 } from 'lucide-react';
import api, { API } from '@/lib/api';
import './rescue-experience.css';

export function Counter({ value, label, visible, reduced, icon: Icon = Users }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!visible || !Number.isFinite(value)) return;
    if (reduced) { setShown(value); return; }
    let frame, start;
    const tick = time => {
      start ??= time;
      const progress = Math.min((time - start) / 1400, 1);
      setShown(Math.round(value * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, visible, reduced]);
  if (!Number.isFinite(value)) return null;
  return <div className="rescue-stat"><Icon aria-hidden="true" className="rescue-stat-icon" /><div><strong aria-hidden="true">{shown.toLocaleString('es-DO')}</strong><span className="sr-only">{value.toLocaleString('es-DO')}</span><span>{label}</span></div></div>;
}

export default function RescueExperience() {
  const [gallery, setGallery] = useState(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const [reduced, setReduced] = useState(false);
  const section = useRef(null);
  useEffect(() => {
    if (gallery?.photos?.length && window.location.hash === '#experiencia') section.current?.scrollIntoView({ block: 'start' });
  }, [gallery]);
  useEffect(() => {
    const controller = new AbortController();
    api.get('/public/rescue-gallery', { signal: controller.signal, timeout: 12000 }).then(({ data }) => setGallery(data)).catch(() => {});
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)') || { matches: false };
    const sync = () => setReduced(media.matches);
    sync(); media.addEventListener?.('change', sync);
    const visibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', visibility);
    return () => { media.removeEventListener?.('change', sync); document.removeEventListener('visibilitychange', visibility); };
  }, []);
  useEffect(() => {
    if (!gallery?.photos?.length || !section.current) return;
    if (!window.IntersectionObserver) { setVisible(true); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: 0.15 });
    observer.observe(section.current);
    return () => observer.disconnect();
  }, [gallery]);
  const photos = gallery?.photos || [];
  useEffect(() => {
    if (photos.length < 2 || paused || hover || focused || reduced || !visible || !pageVisible) return;
    const timer = setInterval(() => setIndex(i => (i + 1) % photos.length), 5000);
    return () => clearInterval(timer);
  }, [photos.length, paused, hover, focused, reduced, visible, pageVisible]);
  if (!photos.length) return null;
  const move = step => setIndex(i => (i + step + photos.length) % photos.length);
  return <section ref={section} id="experiencia" className="section rescue-experience" aria-labelledby="rescue-title">
    <div className="rescue-display" role="region" aria-roledescription="carrusel" aria-label="Fotos de nuestros rescates" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onFocusCapture={() => setFocused(true)} onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}>
      {photos.map((photo, n) => <figure key={photo.id} className={`rescue-slide ${n === index ? 'is-active' : ''}`} aria-hidden={n !== index}><img src={`${API}/public/rescue-gallery/photos/${photo.id}`} alt={photo.alt} style={{ objectPosition: `${photo.position_x}% ${photo.position_y}%`, objectFit: photo.fit }} loading={n === 0 ? 'eager' : 'lazy'} /></figure>)}
      <div className="rescue-overlay"><h2 id="rescue-title">Nuestro trabajo<br /><em>habla por nosotros.</em></h2><p>Experiencia y compromiso en cada operación.</p></div>
      {photos.length > 1 && <><button className="rescue-arrow previous" onClick={() => move(-1)} aria-label="Foto anterior"><ChevronLeft /></button><button className="rescue-arrow next" onClick={() => move(1)} aria-label="Foto siguiente"><ChevronRight /></button><button className="rescue-pause" onClick={() => setPaused(p => !p)} aria-label={paused ? 'Reanudar fotos automáticas' : 'Pausar fotos automáticas'}>{paused ? <Play size={16} /> : <Pause size={16} />}<span>{paused ? 'Reanudar' : 'Pausar'}</span></button></>}
    <div className="rescue-pagination" aria-label="Elegir foto">{photos.map((photo,n) => <button key={photo.id} aria-label={`Ver foto ${n + 1}`} aria-current={n === index ? 'true' : undefined} onClick={() => setIndex(n)} />)}<span aria-live={paused || focused ? 'polite' : 'off'}>{index + 1} / {photos.length}</span></div>
    </div>
    <div className="rescue-stats"><Counter value={gallery.rescues} label="Servicios realizados" visible={visible} reduced={reduced} icon={Users} /><Counter value={gallery.years} label="Años de experiencia" visible={visible} reduced={reduced} icon={CalendarDays} /><Counter value={gallery.units} label="Unidades propias" visible={visible} reduced={reduced} icon={Truck} /><div className="rescue-stat"><Clock3 aria-hidden="true" className="rescue-stat-icon" /><div><strong>24/7</strong><span>Atención disponible</span></div></div></div>
  </section>;
}
